import type { Theme } from "@mui/material";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import HtmlEditor from "@entrilia/shared/components/html/HtmlEditor";
import HtmlPreview from "@entrilia/shared/components/html/HtmlPreview";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { areSameStrings } from "@entrilia/shared/utilities/stringHelper";
import adminApi from "../../../../../api/adminApi";
import type { Fundraising } from "../../../../../api/types/fundraisingTypes";
import useFundraisingDetailsPageContext from "./useFundraisingDetailsPageContext";

const updateFundraising = withErrorHandling(adminApi.updateFundraising);

interface ContentState {
  title: string;
  htmlContent: string;
  initialHtmlContent: string;
  isHtmlContentEdited: boolean;
  isSaving: boolean;
  htmlEditorRenderKey: number;
}

const getInitialContentState = (fundraising: Fundraising) => ({
  title: fundraising.content?.title ?? "",
  htmlContent: fundraising.content?.htmlContent ?? "",
  initialHtmlContent: fundraising.content?.htmlContent ?? "",
  isHtmlContentEdited: false,
  isSaving: false,
  htmlEditorRenderKey: 1,
});

const FundraisingContentConfig = () => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { isContentEditable, fundraising, onUpdated, onConfirmSave } = useFundraisingDetailsPageContext();

  const [state, setState] = useState<ContentState>(() => getInitialContentState(fundraising));

  const save = async () => {
    setState((prev) => ({ ...prev, isSaving: true }));
    const { title, htmlContent } = state;
    const [updatedFundraising, error] = await updateFundraising(fundraising.id, {
      expectedEtag: fundraising.etag,
      content: { title, htmlContent },
    });
    setState((prev) => ({ ...prev, isSaving: false }));

    if (error) {
      logError(error, "[FundraisingContentConfig]");
      sendNotificationError(error.message);
    } else {
      setState((prev) => ({ ...prev, isHtmlContentEdited: false }));
      sendNotification(`Fundraising content updated successfully`);
      onUpdated(updatedFundraising);
    }
  };

  const handleSave = () => onConfirmSave(save);

  const handleReset = () => {
    setState({ ...getInitialContentState(fundraising), htmlEditorRenderKey: -state.htmlEditorRenderKey });
  };

  const handleHtmlContentChange = (value: string) => {
    setState((prev) => ({
      ...prev,
      htmlContent: value,
      isHtmlContentEdited: true,
    }));
  };

  const isEdited = !areSameStrings(state.title, fundraising.content?.title) || state.isHtmlContentEdited;
  const isValid = fundraising.status === "Draft" || Boolean(state.title.trim());
  const showValidationError = isEdited && !isValid;

  if (!isContentEditable) {
    return (
      <Stack spacing={4} sx={{ flex: 1, pt: 1.5 }}>
        <Typography variant="h2">{state.title}</Typography>
        <HtmlPreview htmlContent={fundraising.content?.htmlContent ?? ""} />
      </Stack>
    );
  }

  return (
    <Stack spacing={4} sx={{ flex: 1, pt: 4 }}>
      <TextField
        fullWidth
        placeholder="Add title..."
        value={state.title}
        onChange={(e) => setState((prev) => ({ ...prev, title: e.target.value }))}
        slotProps={{ htmlInput: { sx: (t: Theme) => ({ ...t.typography.h2 }) } }}
        error={showValidationError}
        helperText={showValidationError ? "Title is required" : undefined}
      />
      <Box>
        <HtmlEditor
          key={state.htmlEditorRenderKey}
          initialContent={state.initialHtmlContent}
          onChange={handleHtmlContentChange}
          editorStyles={{ minHeight: "12rem", maxHeight: "24rem" }}
        />
      </Box>
      <Stack direction="row" spacing={1}>
        <Button variant="contained" loading={state.isSaving} onClick={handleSave} disabled={!isEdited || !isValid}>
          Save
        </Button>
        <Button variant="text" color="secondary" onClick={handleReset} disabled={!isEdited}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default FundraisingContentConfig;
